/* eslint-disable no-unused-vars */ // Contains legacy/future-use code kept for potential features
import React, { useEffect, useMemo, useRef, useState } from 'react';
import seed from '../data/controlroom_seed.json';
import { API_BASE_URL } from '../config';

// Resilient fetch with brief retry for transient 404/502/504
async function fetchWithRetry(url, init = {}, retries = 2) {
  let last;
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, init);
      if (!res.ok && (res.status === 404 || res.status === 502 || res.status === 504)) {
        await new Promise(r => setTimeout(r, 250));
        last = new Error(`HTTP ${res.status}`);
        continue;
      }
      return res;
    } catch (e) {
      last = e;
      await new Promise(r => setTimeout(r, 250));
    }
  }
  throw last || new Error('fetch failed');
}

// Unused utility - kept for potential future use
// function toMtString(iso) {
//   if (!iso) return '';
//   try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Denver' }); } catch { return iso; }
// }
function nextChronoMt() {
  const now = new Date();
  const mt = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }));
  const hour = mt.getHours(); const minutes = mt.getMinutes(); const seconds = mt.getSeconds();
  let next = new Date(mt); if (minutes === 0 && seconds === 0) return next; next.setMinutes(0, 0, 0);
  if (hour < 23) { next.setHours(hour + 1); if (hour < 5) next.setHours(6); } else { next.setDate(next.getDate() + 1); next.setHours(6); }
  return next;
}
function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(()=>{ const id=setInterval(()=>setNow(Date.now()),1000); return ()=>clearInterval(id);},[]);
  const diff = Math.max(0, (new Date(target)).getTime() - now);
  const hh = Math.floor(diff / 3600000); const mm = Math.floor((diff % 3600000) / 60000); const ss = Math.floor((diff % 60000) / 1000);
  return <span>{`${hh.toString().padStart(2,'0')}:${mm.toString().padStart(2,'0')}:${ss.toString().padStart(2,'0')}`}</span>;
}

export default function ControlRoom() {
  const [state, setState] = useState(null);
  const [highlights, setHighlights] = useState(() => (Array.isArray(seed?.highlights) ? seed.highlights : []));
  const [initialFast, setInitialFast] = useState(true);
  const [avatars, setAvatars] = useState({});
  const [forecast, setForecast] = useState('chorus');
  const [scoreboard, setScoreboard] = useState({ top_scenes: (seed?.scoreboard?.top_scenes)||[], top_posts: (seed?.scoreboard?.top_posts)||[], top_characters: (seed?.scoreboard?.top_characters)||[], all_time_scene: null, all_time_post: null, all_time_character: null, window_used: 24 });
  const [, setErr] = useState(''); // Used in catch block, value not read
  // Persist the active cue for up to 10 minutes after it first appears
  const [persistedCue, setPersistedCue] = useState(null);
  const [persistedSince, setPersistedSince] = useState(0);
  const [liveFeed, setLiveFeed] = useState([]);
  const insightsCacheRef = useRef({});

  useEffect(() => {
    let alive = true;
    // Hydrate highlights immediately from local cache for instant first paint
    try {
      const cached = JSON.parse(localStorage.getItem('recentScenes') || '[]');
      if (Array.isArray(cached) && cached.length && alive) setHighlights(cached);
    } catch {}
    const fetchState = async () => {
      try {
        const r = await fetchWithRetry(`${API_BASE_URL}/api/state`);
        const dj = await r.json();
        if (alive) {
          setState(dj);
          const cs = dj && dj.current_scene;
          if (cs && cs.cue_id) {
            // If a new cue appears, start (or refresh) the 10-minute persistence window
            if (!persistedCue || persistedCue.cue_id !== cs.cue_id) {
              setPersistedCue(cs);
              setPersistedSince(Date.now());
            }
          }
        }
      } catch (e) {
        if (alive) setErr('Live unavailable');
      }
    };
    const fetchHighlights = async () => {
      try {
        const r = await fetchWithRetry(`${API_BASE_URL}/api/highlights/recent?limit=8&hours=72`);
        const dj = await r.json();
        let arr = (dj && (dj.highlights || dj.rows)) || [];
        // Ensure at least 6 by expanding window if needed
        if (!Array.isArray(arr) || arr.length < 6) {
          try {
            const r2 = await fetchWithRetry(`${API_BASE_URL}/api/highlights/recent?limit=10&hours=168`);
            const dj2 = await r2.json();
            const arr2 = (dj2 && (dj2.highlights || dj2.rows)) || [];
            const seen = new Set((arr||[]).map(x => x.scene_id));
            for (const it of (arr2||[])) { if (!seen.has(it.scene_id)) { arr.push(it); seen.add(it.scene_id); if (arr.length>=6) break; } }
          } catch {}
        }
        // Shuffle once to avoid repeating the same first scene
        if (Array.isArray(arr)) {
          const copy = arr.slice();
          for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
          }
          if (alive) {
            setHighlights(copy);
            try { localStorage.setItem('recentScenes', JSON.stringify(copy)); } catch {}
            // Prefetch nearest quotes for first lines of each scene
            try {
              const texts = [];
              for (const sc of copy) {
                const posts = (sc.posts || []).slice(0, 3);
                for (const p of posts) {
                  const t = String(p.content || '').trim();
                  if (t) texts.push(t.slice(0, 200));
                }
              }
              if (texts.length) {
                const rr = await fetch(`${API_BASE_URL}/api/feed/insights/batch`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ texts })});
                if (rr.ok) {
                  const dj3 = await rr.json();
                  const items = Array.isArray(dj3?.items) ? dj3.items : [];
                  const cache = insightsCacheRef.current;
                  for (let i=0;i<texts.length && i<items.length;i++) {
                    cache[texts[i]] = items[i];
                  }
                }
              }
            } catch {}
          }
        } else {
          if (alive) setHighlights([]);
        }
      } catch {}
    };
    const fetchAvatars = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/ui/chat/avatars`); const dj = await r.json(); if (alive) setAvatars(dj || {}); } catch (e) { console.error('avatars load failed', e); } };
    const fetchForecast = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/forecast/next-cue`); const dj = await r.json(); if (alive) setForecast((dj && dj.forecast) || 'chorus'); } catch (e) { console.error('forecast load failed', e); } };
    const fetchScoreboard = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/insights/scoreboard?window_hours=72&limit=6`); const dj = await r.json(); if (alive) setScoreboard(dj || {}); } catch (e) { console.error('scoreboard load failed', e); } };
    const fetchSummary = async () => { /* intentionally disabled to reduce load */ };
    const fetchTopLines = async () => { /* intentionally disabled to reduce load */ };
    const fetchLive = async () => { try { const r = await fetchWithRetry(`${API_BASE_URL}/api/live/feed?hours=48&limit=300`); const dj = await r.json(); if (alive) setLiveFeed((dj && dj.posts) || []); } catch (e) { console.error('live feed load failed', e); } };
    fetchState(); fetchHighlights(); fetchAvatars(); fetchForecast(); fetchScoreboard(); fetchSummary(); fetchTopLines(); fetchLive();
    const id1 = setInterval(fetchState, 5000);
    const id2 = setInterval(fetchHighlights, 60000);
    const id3 = setInterval(fetchForecast, 60000);
    const id4 = setInterval(fetchScoreboard, 120000);
    const id5 = setInterval(fetchSummary, 120000);
    const id6 = setInterval(fetchTopLines, 120000);
    const id7 = setInterval(fetchLive, 15000);
    return () => { alive = false; [id1,id2,id3,id4,id5,id6,id7].forEach(clearInterval); };
  }, [persistedCue]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowMs = Date.now();
  const rawActive = state?.current_scene;
  const effectiveActive = rawActive || ((persistedCue && (nowMs - persistedSince) < 10 * 60 * 1000) ? persistedCue : null);
  const cueText = effectiveActive?.cue_text || '';
  const nextChrono = useMemo(() => nextChronoMt(), []); // No dependencies needed - pure function

  return (
    <div className="console-container">
      <div className="console-left">
        <div className="console-header">Troupe Scoreboard</div>
        <div className="console-panel twinkle">
          <div className="console-countdowns">
            <div className="tile">Next Cue <div className="time"><Countdown target={nextChrono} /></div></div>
            <div className="tile">Forecast <div className="time">{forecast}</div></div>
          </div>
          <ScoreboardTicker data={scoreboard} />
        </div>
      </div>
      <div className="console-right">
        <div className="console-header">Highlights Feed (last 72 hours) | For full live feed, follow Discord channel <a href="https://discord.gg/3Mju8yEHPD" target="_blank" rel="noreferrer" style={{color:'#60a5fa'}}>“Bitbard: AI Powered Shakespeare.”</a></div>
        <div className="console-panel rotator">
          {!Array.isArray(highlights) || highlights.length === 0 ? (
            <div className="line loading-pink" aria-live="polite">Loading<span className="loading-dots"><span className="loading-dot">.</span><span className="loading-dot">.</span><span className="loading-dot">.</span></span></div>
          ) : null}
          <LiveFeedRotator
            posts={liveFeed}
            avatars={avatars}
            activeCueId={effectiveActive?.cue_id || ''}
            fallbackScenes={highlights}
            cueText={cueText}
            initialFast={initialFast}
            onFirstPaint={() => setInitialFast(false)}
          />
        </div>
      </div>
    </div>
  );
}

function ScoreboardTicker({ data }) {
  const chars = data?.top_characters || [];
  const bestScene = data?.all_time_scene;
  const bestPost = data?.all_time_post;
  const bestChar = data?.all_time_character;
  const [avatars, setAvatars] = useState({});
  
  // Load avatars for character display
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/ui/chat/avatars`);
        if (r.ok) setAvatars(await r.json());
      } catch {}
    })();
  }, []);
  
  const scenesOrdered = useMemo(() => {
    const scenes = data?.top_scenes || []; // Move inside useMemo to avoid recreating dependency
    const arr = Array.isArray(scenes) ? scenes.slice() : [];
    // Non-zero scores first, then by score desc
    return arr.sort((a,b) => (Number(!!b.score) - Number(!!a.score)) || ((b.score||0) - (a.score||0)));
  }, [data?.top_scenes]);
  const postsOrdered = useMemo(() => {
    const posts = data?.top_posts || []; // Move inside useMemo to avoid recreating dependency
    const arr = Array.isArray(posts) ? posts.slice() : [];
    return arr.sort((a,b) => (Number(!!b.reactions) - Number(!!a.reactions)) || ((b.reactions||0) - (a.reactions||0)));
  }, [data?.top_posts]);
  return (
    <div style={{ marginTop: 12, maxHeight: '65vh', overflowY: 'auto' }} className="console-panel">
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700 }}>Scoreboard</div>
      <div className="line" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {bestScene && (
          <div className="tile" style={{ 
            minWidth: 280, 
            background: 'linear-gradient(135deg, #1a3a52 0%, #0f2538 100%)',
            border: '2px solid #2a5a7a',
            padding: 14,
            borderRadius: 8
          }}>
            <div style={{ color: '#9bdcff', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              🏆 All-Time Top Scene
            </div>
            <div><a href={bestScene.web_url || '#'} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>View scene →</a></div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#ffb2e4' }}>Score: {bestScene.score || '—'}</div>
          </div>
        )}
        {bestPost && (
          <div className="tile" style={{ 
            minWidth: 280,
            background: 'linear-gradient(135deg, #1a2a52 0%, #0f1a38 100%)',
            border: '2px solid #2a4a7a',
            padding: 14,
            borderRadius: 8
          }}>
            <div style={{ color: '#ffb2e4', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              🏆 All-Time Top Post
            </div>
            <div><a href={bestPost.url || '#'} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>View post →</a></div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#9bdcff' }}>
              {bestPost.author} · {bestPost.reactions ? `+${bestPost.reactions}` : '—'}
            </div>
          </div>
        )}
        {bestChar && (
          <div className="tile" style={{ minWidth: 220 }}>
            <div style={{ color: '#7aa4c9' }}>All-time Character</div>
            <div style={{ fontWeight: 700 }}>
              <a href={bitbardProfileUrl(bestChar.character)} target="_blank" rel="noreferrer">{bestChar.character}</a>
            </div>
            <div style={{ fontSize: 12 }}>Score {bestChar.score}</div>
          </div>
        )}
      </div>
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700, marginTop: 6 }}>Top Scenes (72h)</div>
      <div className="line" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {scenesOrdered.map((s, i) => {
          // Smart display: Use most informative context available
          let displayText;
          
          if (s.target_title) {
            // Promote scene - show what they're responding to
            displayText = `🎭 Cue: Response to: ${s.target_title.slice(0, 70)}${s.target_title.length > 70 ? '...' : ''}`;
          } else if (s.preview_response) {
            // Other scene with responses - show cue text
            const cuePreview = (s.incipit || '').replace(/🎭 Cue: (Chorus|Wildcard|Solo|Duel) \| /, '');
            displayText = `🎭 Cue: ${cuePreview.slice(0, 70)}${cuePreview.length > 70 ? '...' : ''}`;
          } else {
            // Fallback: show incipit with prefix
            const clean = (s.incipit || s.snippet || 'View scene').replace(/🎭 Cue: (Chorus|Wildcard|Solo|Duel) \| /, '');
            displayText = `🎭 Cue: ${clean.slice(0, 70)}`;
          }
          
          return (
            <div key={i} className="tile" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <a href={s.web_url || '#'} target="_blank" rel="noreferrer" style={{ lineHeight: 1.4 }}>
                {displayText}
              </a>
              <span style={{ fontSize: 12, opacity: 0.8 }}>Score {s.score ? `±${s.score}` : '— Unscored'}</span>
            </div>
          );
        })}
      </div>
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700, marginTop: 6 }}>Top Posts (72h)</div>
      <div className="line" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {postsOrdered.map((p, i) => (
          <div key={`p-${i}`} className="tile" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <a href={(p.url || p.scene_url || '#')} target="_blank" rel="noreferrer" style={{ lineHeight: 1.4 }}>
              "{(p.content || '').slice(0, 80)}{(p.content || '').length > 80 ? '...' : ''}"
            </a>
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              {p.author} · {p.reactions ? `+${p.reactions}` : '— Unscored'}
            </span>
          </div>
        ))}
      </div>
      <div className="line" style={{ color: '#9bdcff', fontWeight: 700, marginTop: 6 }}>Character Ranks (72h)</div>
      <div className="line" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {chars.map((c, i) => (
          <div key={`c-${i}`} className="tile" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CharacterAvatar name={c.character} avatars={avatars} size="md" />
            <div style={{ flex: 1 }}>
              <a href={bitbardProfileUrl(c.character)} target="_blank" rel="noreferrer">{c.character}</a>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Score {c.score}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reusable character avatar component
function CharacterAvatar({ name, avatars, size = 'sm' }) {
  const cleanName = String(name || '').replace(/[🎭🍷🗡🎪🎨]/g, '').trim();
  const key = cleanName.toLowerCase();
  
  // Match avatar from avatars object
  let avatarUrl = null;
  if (key.startsWith('falstaff') && avatars['falstaff']) avatarUrl = avatars['falstaff'];
  else if (key.startsWith('lady macbeth') && avatars['lady macbeth']) avatarUrl = avatars['lady macbeth'];
  else if (key.startsWith('sir andrew') && (avatars['sir andrew'] || avatars['sir andrew aguecheek'])) {
    avatarUrl = avatars['sir andrew'] || avatars['sir andrew aguecheek'];
  }
  else if (key.startsWith('miranda') && avatars['miranda']) avatarUrl = avatars['miranda'];
  else if (key.startsWith('iago') && avatars['iago']) avatarUrl = avatars['iago'];
  else if (key.startsWith('horatio') && avatars['horatio']) avatarUrl = avatars['horatio'];
  else if (key.startsWith('bitbard') && avatars['bitbard']) avatarUrl = avatars['bitbard'];
  
  const dimensions = size === 'sm' ? 20 : 28;
  
  if (avatarUrl) {
    return (
      <img 
        src={avatarUrl} 
        alt={name}
        title={name}
        style={{ 
          width: dimensions, 
          height: dimensions, 
          borderRadius: '50%',
          marginRight: 4,
          display: 'inline-block',
          verticalAlign: 'middle',
          objectFit: 'cover'
        }}
      />
    );
  }
  
  // Fallback: initials
  const parts = cleanName.split(/\s+/);
  const initials = (parts[0]?.[0] || '?') + (parts[1]?.[0] || '');
  let h = 0; 
  for (let i=0;i<cleanName.length;i++) h = (h*31 + cleanName.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const bg = `hsl(${hue} 45% 22%)`;
  
  return (
    <div 
      title={name}
      style={{ 
        width: dimensions, 
        height: dimensions, 
        borderRadius: '50%',
        background: bg,
        color: '#e6eef6',
        fontSize: size === 'sm' ? 9 : 11,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 4,
        verticalAlign: 'middle',
        fontWeight: 500
      }}
    >
      {initials.toUpperCase()}
    </div>
  );
}

function bitbardProfileUrl(character) {
  try {
    // Prefer Bluesky profiles
    const bskyBase = 'https://bsky.app/profile/';
    const mapBsky = {
      'falstaff': 'bbo-falstaff.bsky.social',
      'iago': 'bbo-iago.bsky.social',
      'lady macbeth': 'bbo-ladymacbeth.bsky.social',
      'miranda': 'bbo-miranda.bsky.social',
      'sir andrew': 'bbo-sirandrew.bsky.social'
    };
    const key = (character || '').toLowerCase().trim();
    if (mapBsky[key]) return `${bskyBase}${mapBsky[key]}`;
    // Fallback to bitbard.io
    const bb = 'https://bitbard.io/';
    const slug = `bbo_${key.replace(/\s+/g,'')}`;
    return `${bb}${slug}/`;
  } catch { return '#'; }
}

// (unused) RotatorCard

// Teletype-style replay with per-character typing over full scene
function TeletypeReplay({ item, avatars, onDone, topPosts, alwaysDazzle, fastFirst }) {
  const posts = useMemo(() => (item?.posts || []), [item?.posts]); // Memoize to prevent recreation on every render
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [forcedDazzle, setForcedDazzle] = useState(false);
  const wrapRef = useRef(null);
  const [hover, setHover] = useState({ show: false, x: 0, y: 0, html: '' });
  const insightCacheRef = useRef({});
  const [sourceUrl, setSourceUrl] = useState('');
  // Load source sidecar for promote scenes to render "Responding to" link
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const sid = item?.scene_id;
        if (!sid) { setSourceUrl(''); return; }
        const r = await fetch(`${API_BASE_URL}/api/archive/evidence?scene_id=${encodeURIComponent(String(sid))}`);
        if (r.ok) {
          const dj = await r.json();
          const src = (dj && dj.source && (dj.source.bsky_url || dj.source.url)) || '';
          if (alive) setSourceUrl(src || '');
        } else {
          if (alive) setSourceUrl('');
        }
      } catch { if (alive) setSourceUrl(''); }
    })();
    return () => { alive = false };
  }, [item?.scene_id]);

  // Preserve original order from highlight scene
  const ordered = useMemo(() => {
    return Array.isArray(posts) ? posts : [];
  }, [posts]);

  // detect if first line is BitBard cue incipit
  const incipitIsFirst = useMemo(() => {
    if (!ordered.length) return false;
    const a = (ordered[0]?.author || '');
    const c = (ordered[0]?.content || '');
    return a.startsWith('BitBard') && /^🎭\s*Cue:\s*/.test(c);
  }, [ordered]);

  // helper: detect curtain line variants (colon, em dash, hyphen; Curtain or Curtain Call)
  const isCurtainLine = (txt) => {
    try {
      // Remove zero-width and formatting chars, collapse spaces
      const cleaned = String(txt || '')
        .replace(/[\u200B-\u200D\u2060-\u206F]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      // match: "Cue: Curtain", "Cue — Curtain", "Cue - Curtain", and "Curtain Call" variants
      if (/\bCue\b\s*[:—-]\s*Curtain(?:\s*Call)?\b/i.test(cleaned)) return true;
      // fallback: standalone word 'Curtain' near start or as sole token
      if (/^(?:Curtain(?:\s*Call)?)(?:\b|$)/i.test(cleaned)) return true;
      return false;
    } catch { return false; }
  };

  // build tech caption like "Southwark, 9:56 pm GMT, 1 day ago"
  const caption = useMemo(() => {
    const iso = item?.posted_at || (item?.posts && item.posts[0]?.created_at) || (item?.posts && item.posts[0]?.timestamp);
    if (!iso) return '';
    try {
      const dt = new Date(iso);
      const timeStr = dt.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).toLowerCase();
      const now = new Date();
      const days = Math.floor((now.getTime() - dt.getTime()) / (24*3600*1000));
      const rel = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
      return `Southwark, ${timeStr} GMT, ${rel}`;
    } catch {
      return '';
    }
  }, [item]);

  // Build a stable signature of the posts to avoid resetting on harmless poll updates
  const itemSig = useMemo(() => {
    try {
      return (item?.posts || []).map(p => `${p.author}|${p.content}|${p.timestamp||p.created_at||''}`).join('§');
    } catch { return String((item && item.posts && item.posts.length) || 0); }
  }, [item]);

  // reset only when the content signature changes (prevents restart on identical polls)
  useEffect(() => {
    if (!ordered.length) return;
    setLineIndex(incipitIsFirst ? 1 : 0);
    setCharIndex(0);
    setFinished(false);
  }, [itemSig, incipitIsFirst, ordered.length]);

  // per-character typing: ~10s per post regardless of length; sprint on first paint
  useEffect(() => {
    if (!ordered.length) return;
    if (lineIndex >= ordered.length) return;
    const text = (ordered[lineIndex]?.content || '');
    if (charIndex >= text.length) {
      const t = setTimeout(() => {
        if (lineIndex >= ordered.length - 1) {
          setFinished(true);
        } else {
          setLineIndex(lineIndex + 1);
          setCharIndex(0);
        }
      }, 300);
      return () => clearTimeout(t);
    }
    const duration = fastFirst ? 800 : 4000;  // Reduced from 9000 to 4000 for faster typing
    const step = Math.max(8, Math.floor(duration / Math.max(1, text.length)));
    const id = setTimeout(() => setCharIndex(ci => ci + 1), step);
    return () => clearTimeout(id);
  }, [ordered, lineIndex, charIndex, fastFirst]);

  // auto-scroll as text grows
  useEffect(() => {
    if (!wrapRef.current) return;
    try { wrapRef.current.scrollTop = wrapRef.current.scrollHeight; } catch {}
  }, [lineIndex, charIndex]);

  // trigger rotation after finish: if curtain, wait for CurtainSequence callback; else brief pause or forced dazzle
  useEffect(() => {
    if (!finished) return;
    const lastLine = (ordered[ordered.length-1]?.content)||'';
    if (isCurtainLine(lastLine)) return; // CurtainSequence will signal onComplete
    if (alwaysDazzle && !forcedDazzle) { setForcedDazzle(true); return; }
    const t = setTimeout(() => { onDone && onDone(); }, 2000);  // Increased from 500ms to 2000ms for better readability
    return () => clearTimeout(t);
  }, [finished, alwaysDazzle, forcedDazzle, onDone, ordered]); // Added missing dependencies

  // const activePink = '#ff3ab5'; // Unused - kept for potential future use
  const idleCyan = '#9bdcff';
  return (
    <div className="teletype" ref={wrapRef}>
      {sourceUrl && (
        <div className="line" style={{ color: '#9bdcff' }}>
          Responding to: <a href={sourceUrl} target="_blank" rel="noreferrer">view target card</a>
        </div>
      )}
      {!!caption && <div className="teletype-caption">{caption}</div>}
      {incipitIsFirst && ordered[0] && (
        <div className="line" style={{ color: idleCyan }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(ordered[0].author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{ordered[0].author}:</strong> {ordered[0].content}
        </div>
      )}
      {ordered.slice(incipitIsFirst ? 1 : 0, Math.min(lineIndex, ordered.length)).map((p, i) => (
        <div
          key={i}
          className="line"
          style={{ color: idleCyan }}
          onMouseEnter={async (e) => {
            try {
              const text = String(p.content || '').slice(0, 200);
              if (!text) return;
              const cache = insightCacheRef.current;
              if (!cache[text]) {
                const r = await fetch(`${API_BASE_URL}/api/feed/insights`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ text })});
                if (r.ok) cache[text] = await r.json(); else cache[text] = null;
              }
              const nq = cache[text]?.nearest_quote;
              if (nq) {
                const cite = String(nq.cite || '');
                const idx = cite.lastIndexOf(',');
                const speakerLoc = idx >= 0 ? cite.slice(0, idx) : cite;
                const title = idx >= 0 ? cite.slice(idx + 1).trim() : '';
                const html = `Closest Shakespeare (pilot): "${nq.text}" (${speakerLoc}${title ? `; <em>${title}</em>` : ''})`;
                setHover({ show: true, x: e.clientX + 12, y: e.clientY + 12, html });
              } else {
                setHover({ show: true, x: e.clientX + 12, y: e.clientY + 12, html: '' });
              }
            } catch {}
          }}
          onMouseMove={(e)=> hover.show && setHover(h=>({ ...h, x: e.clientX + 12, y: e.clientY + 12 }))}
          onMouseLeave={()=> setHover(h=>({ ...h, show:false }))}
        >
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(p.author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{p.author}:</strong> {p.content}
        </div>
      ))}
      {lineIndex < ordered.length && (
        <div
          className="line"
          style={{ color: '#ff3ab5' }}
          onMouseEnter={async (e) => {
            try {
              const text = String(ordered[lineIndex]?.content || '').slice(0, 200);
              if (!text) return;
              const cache = insightCacheRef.current;
              if (!cache[text]) {
                const r = await fetch(`${API_BASE_URL}/api/feed/insights`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ text })});
                if (r.ok) cache[text] = await r.json(); else cache[text] = null;
              }
              const nq = cache[text]?.nearest_quote;
              if (nq) {
                const cite = String(nq.cite || '');
                const idx = cite.lastIndexOf(',');
                const speakerLoc = idx >= 0 ? cite.slice(0, idx) : cite;
                const title = idx >= 0 ? cite.slice(idx + 1).trim() : '';
                const html = `Closest Shakespeare (pilot): "${nq.text}" (${speakerLoc}${title ? `; <em>${title}</em>` : ''})`;
                setHover({ show: true, x: e.clientX + 12, y: e.clientY + 12, html });
              } else {
                setHover({ show: true, x: e.clientX + 12, y: e.clientY + 12, html: '' });
              }
            } catch {}
          }}
          onMouseMove={(e)=> hover.show && setHover(h=>({ ...h, x: e.clientX + 12, y: e.clientY + 12 }))}
          onMouseLeave={()=> setHover(h=>({ ...h, show:false }))}
        >
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(ordered[lineIndex].author, avatars)})` }} />
          <strong style={{ color: '#ffd6ef' }}>{ordered[lineIndex].author}:</strong> {(ordered[lineIndex].content || '').slice(0, charIndex)}
          <span className="caret">▮</span>
        </div>
      )}
      {hover.show && (
        <div className="dna-popover" style={{ left: hover.x, top: hover.y }}>
          <div dangerouslySetInnerHTML={{ __html: hover.html }} />
        </div>
      )}
      {lineIndex >= ordered.length - 1 && finished && (
        isCurtainLine((ordered[ordered.length-1]?.content)||'') || forcedDazzle ? (
          <div style={{ marginTop: 10 }}>
            <div className="cylon-wrap"><div className="cylon-eye" /></div>
            {/* Hold for 10s then complete */}
            <DelayComplete ms={10000} onDone={() => { setForcedDazzle(false); onDone && onDone(); }} />
          </div>
        ) : null
      )}
      {item?.web_url && <div style={{ marginTop: 8 }}><a className="bsky-btn" href={item.web_url} target="_blank" rel="noreferrer">Open on Bluesky</a></div>}
    </div>
  );
}

// Live teletype that types only the newest incoming line, leaves prior lines steady, and autoscrolls down
function LiveTeletype({ posts, avatars }) {
  const ordered = Array.isArray(posts) ? posts.slice().reverse() : [];// oldest first not guaranteed; reverse recent desc to asc
  const [charIndex, setCharIndex] = useState(0);
  const wrapRef = useRef(null);

  // Advance typing when new posts arrive
  useEffect(() => {
    if (!Array.isArray(posts) || posts.length === 0) return;
    setCharIndex(0);
  }, [posts]);

  // Type only the newest line
  useEffect(() => {
    if (!Array.isArray(posts) || posts.length === 0) return;
    const latest = posts[posts.length - 1];
    const text = (latest?.content || '');
    if (charIndex >= text.length) return;
    const step = Math.max(12, Math.floor(6000 / Math.max(1, text.length)));
    const id = setTimeout(() => setCharIndex(c => c + 1), step);
    return () => clearTimeout(id);
  }, [posts, charIndex]);

  // Autoscroll to bottom
  useEffect(() => {
    if (!wrapRef.current) return;
    try { wrapRef.current.scrollTop = wrapRef.current.scrollHeight; } catch {}
  }, [posts, charIndex]);

  const idleCyan = '#9bdcff';
  return (
    <div className="teletype" ref={wrapRef} style={{ maxHeight: 260, overflowY: 'auto', marginTop: 6 }}>
      {(posts || []).slice(0, Math.max(0, posts.length - 1)).map((p, i) => (
        <div key={`live-${i}`} className="line" style={{ color: idleCyan }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(p.author, avatars)})` }} />
          <strong style={{ color: '#e6eefc' }}>{p.author}:</strong> {p.content}
        </div>
      ))}
      {Array.isArray(posts) && posts.length > 0 && (
        <div className="line" style={{ color: '#ff3ab5' }}>
          <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(posts[posts.length - 1].author, avatars)})` }} />
          <strong style={{ color: '#ffd6ef' }}>{posts[posts.length - 1].author}:</strong> {(posts[posts.length - 1].content || '').slice(0, charIndex)}
          <span className="caret">▮</span>
        </div>
      )}
    </div>
  );
}

// Rotates the five most recent live posts as a mini-scene with teletype reveal.
function LiveFeedRotator({ posts, avatars, activeCueId, fallbackScenes, cueText, initialFast, onFirstPaint }) {
  // Group posts by scene_id; ensure BitBard cue first, then replies in chronological order
  const scenes = useMemo(() => {
    const arr = Array.isArray(posts) ? posts.slice() : [];
    const byScene = new Map();
    for (const p of arr) {
      const sid = p.scene_id || p.scene || p.thread_id || 'misc';
      if (!byScene.has(sid)) byScene.set(sid, []);
      byScene.get(sid).push(p);
    }
      const orderedScenes = Array.from(byScene.entries()).map(([sid, list]) => {
      // Separate incipit (first BitBard), character replies, and curtain (last BitBard)
      const bitbardPosts = list.filter(x => (x.author||'').startsWith('BitBard'))
        .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
      const incipit = bitbardPosts[0]; // First BitBard post is cue
      const curtain = bitbardPosts.length > 1 ? bitbardPosts[bitbardPosts.length - 1] : null; // Last BitBard post is curtain
      const replies = list.filter(x => !(x.author||'').startsWith('BitBard'))
        .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
      // Build complete scene: incipit → character replies → curtain
      let postsOrdered = [incipit, ...replies, curtain].filter(Boolean);
      // If incomplete or misordered (no incipit or curtain first), try to merge from highlights fallback
      const looksLikeCurtain = (txt) => /\bCurtain(\s*Call)?\b/i.test(String(txt||''));
      if (((!incipit) || looksLikeCurtain(postsOrdered[0]?.content) || postsOrdered.length < 3) && Array.isArray(fallbackScenes)) {
        const hit = fallbackScenes.find(h => String(h.scene_id) === String(sid));
        if (hit && Array.isArray(hit.posts)) {
          const seen = new Set(postsOrdered.map(pp => (pp.author||'')+ '|' + (pp.content||'')));
          const more = hit.posts
            .map(p => ({ author: p.author, content: p.content, created_at: p.timestamp, timestamp: p.timestamp }))
            .filter(p => { const key = (p.author||'')+'|'+(p.content||''); if (seen.has(key)) return false; seen.add(key); return true; })
            .sort((a,b) => (new Date(a.created_at||a.timestamp||0)) - (new Date(b.created_at||b.timestamp||0)));
          // Ensure incipit first
          const inc2 = more.find(x => (x.author||'').startsWith('BitBard'));
          const rest2 = more.filter(x => !(x.author||'').startsWith('BitBard'));
          postsOrdered = [incipit || inc2, ...replies, ...rest2].filter(Boolean);
          // If still short, keep appending remaining highlight posts
          if (postsOrdered.length < 3) {
            postsOrdered = [incipit || inc2, ...more.filter(Boolean)];
          }
        }
      }
      // Guard: require incipit first and avoid curtain-only stubs
      if (!postsOrdered.length || !(postsOrdered[0]?.author||'').startsWith('BitBard')) return null;
      if (looksLikeCurtain(postsOrdered[0]?.content)) return null;
      const postedAt = (incipit && (incipit.created_at || incipit.timestamp)) || (list[list.length-1]?.timestamp) || null;
      return { posts: postsOrdered, posted_at: postedAt, scene_id: sid };
    });
    let cleaned = orderedScenes.filter(Boolean);
    cleaned.sort((a,b) => (new Date(b.posted_at||0)) - (new Date(a.posted_at||0)));
    // Filter out incomplete scenes (must have at least 3 posts: incipit + 1 response + curtain)
    cleaned = cleaned.filter(sc => sc.posts && sc.posts.length >= 3);
    cleaned = cleaned.slice(0, 5);
    
    // Top-up from highlights so the carousel always has 5 scenes ready (last 72h)
    if (Array.isArray(fallbackScenes) && fallbackScenes.length) {
      const seenSig = new Set(cleaned.map(s => (s.posts && s.posts[0] ? ((s.posts[0].author||'') + '|' + (s.posts[0].content||'')) : '')));
      for (const h of fallbackScenes) {
        if (cleaned.length >= 5) break;
        const posts = Array.isArray(h.posts) ? h.posts.map(p => ({ author: p.author, content: p.content, created_at: p.timestamp, timestamp: p.timestamp })) : [];
        // Also filter fallback scenes
        if (posts.length < 3) continue;
        const sig = posts.length ? ((posts[0].author||'') + '|' + (posts[0].content||'')) : '';
        if (sig && !seenSig.has(sig)) {
          cleaned.push({ posts, posted_at: h.posted_at || (h.posts && h.posts[0] && h.posts[0].timestamp) || null });
          seenSig.add(sig);
        }
      }
    }
    return cleaned.slice(0,5);
  }, [posts, fallbackScenes]);

  const [index, setIndex] = useState(0);
  useEffect(() => { setIndex(0); }, [activeCueId]);
  
  // If a live cue is active, prioritize the scene whose incipit matches the cue text
  const prioritizedScenes = useMemo(() => {
    if (!Array.isArray(scenes) || !scenes.length) return scenes;
    try {
      const cx = (cueText || '').toLowerCase().slice(0, 80);
      const liveIdx = scenes.findIndex(sc => (
        Array.isArray(sc.posts) && sc.posts.length > 0 &&
        (sc.posts[0].author || '').startsWith('BitBard') &&
        ((sc.posts[0].content || '').toLowerCase().includes(cx))
      ));
      if (liveIdx >= 0) {
        const copy = scenes.slice();
        const [live] = copy.splice(liveIdx, 1);
        return [live, ...copy];
      }
      return scenes;
    } catch { return scenes; }
  }, [scenes, cueText]);
  
  // Add failsafe timeout to prevent infinite hang on a scene (must come AFTER prioritizedScenes definition)
  useEffect(() => {
    const maxTime = 45000; // 45 seconds max per scene
    const timer = setTimeout(() => {
      console.warn('[LiveFeedRotator] Scene display timeout, advancing to next');
      setIndex(i => (i + 1) % Math.max(1, prioritizedScenes.length));
    }, maxTime);
    return () => clearTimeout(timer);
  }, [index, prioritizedScenes.length]);

  const item = prioritizedScenes[index] || { posts: [] };
  useEffect(() => { if (initialFast && prioritizedScenes.length) { try { onFirstPaint && onFirstPaint(); } catch {} } }, [initialFast, prioritizedScenes, onFirstPaint]);
  const handleDone = () => {
    setTimeout(() => setIndex(i => (i + 1) % Math.max(1, prioritizedScenes.length || 1)), 250);
  };
  return (
    <TeletypeReplay
      key={(item.posted_at||'') + ':' + index}
      item={item}
      avatars={avatars}
      topPosts={[]}
      onDone={handleDone}
      alwaysDazzle
      fastFirst={initialFast}
    />
  );
}

// Build a lightweight, console-style Scene DNA text block for hover popover
function buildSceneDNAText(item) {
  try {
    const posts = Array.isArray(item?.posts) ? item.posts : [];
    const incipit = posts.find(p => (p.author||'').startsWith('BitBard'));
    const firstReply = posts.find(p => !(p.author||'').startsWith('BitBard'));
    const headline = (incipit?.content || '').slice(0, 180);
    const replySnip = (firstReply?.content || '').slice(0, 120);
    const host = (item?.story_url || '').split('/')[2] || (item?.web_url || '').split('/')[2] || '';
    const embed = host ? 'External' : 'Record/Image';
    const llm = 'off';
    const bars = (v) => {
      const n = Math.max(0, Math.min(10, Math.round(v * 10)));
      return '▰'.repeat(n) + '▏'.repeat(10 - n);
    };
    // Fake node confidences heuristically from content
    const txt = `${incipit?.content || ''} ${replySnip}`.toLowerCase();
    const sc = /coach|team|game|goal|score/.test(txt) ? 0.82 : /king|duke|crown/.test(txt) ? 0.58 : 0.41;
    const hc = /honor|duty|vow/.test(txt) ? 0.66 : 0.48;
    const fc = /fate|omens|prophecy|doom/.test(txt) ? 0.44 : 0.32;
    const seed = Math.min(0.85, 0.30 + sc * 0.3 + hc * 0.2);
    const novel = Math.max(0.15, 1 - seed);
    const lines = [];
    lines.push(`🎭 Scene DNA — ${host || 'bitbard'} • ${item?.cue_type || 'Chorus'}`);
    lines.push(`🏷️ Source: highlights • 🌐 ${host || '—'} • 🧵 Embed: ${embed} • 🤖 LLM: ${llm}`);
    if (headline) lines.push(`🧪 Seeds: ${headline}`);
    if (replySnip) lines.push(`🗣️ First reply: ${replySnip}`);
    // Remove heuristic nodes line
    // lines.push(`🧬 Nodes: sports(${sc.toFixed(2)}) ${bars(sc)}  honor(${hc.toFixed(2)}) ${bars(hc)}  fate(${fc.toFixed(2)}) ${bars(fc)}`);
    lines.push(`📈 Coverage: Seed ${(seed*100|0)}% | Novel ${(novel*100|0)}%  ${bars(seed)}`);
    if (item?.web_url) lines.push(`🔗 Root: ${item.web_url}`);
    return lines.join('\n');
  } catch { return 'Scene DNA unavailable'; }
}

// A single line with avatar + hover DNA popover derived from its text
function LineWithDNA({ author, text, displayText, avatars, color, typing, topPosts, insightsCacheRef }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dna, setDna] = useState('');
  const [insight, setInsight] = useState(null);
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className="line"
      style={{ color: color || '#9bdcff', position: 'relative' }}
        onMouseEnter={async (e) => {
        setDna(buildLineDNAText(author, text, topPosts));
        setPos({ x: e.clientX + 12, y: e.clientY + 12 });
        setShow(true);
          const key = String(text || '').slice(0, 200);
          const pref = insightsCacheRef.current[key];
          if (pref) { setInsight(pref); return; }
        // Fetch nearest quote insight (best-effort)
        try {
          const r = await fetch(`${API_BASE_URL}/api/feed/insights`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ text })});
          if (r.ok) {
            const dj = await r.json();
            setInsight(dj || null);
              insightsCacheRef.current[key] = dj || null;
          }
        } catch {}
      }}
      onMouseMove={(e) => show && setPos({ x: e.clientX + 12, y: e.clientY + 12 })}
      onMouseLeave={() => setShow(false)}
    >
      <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(author, avatars)})` }} />
      <strong style={{ color: color ? (color === '#ff3ab5' ? '#ffd6ef' : '#e6eefc') : '#e6eefc' }}>{author}:</strong> {displayText != null ? displayText : text}
      {typing && <span className="caret">▮</span>}
      {show && (
        <div className="dna-popover" style={{ left: pos.x, top: pos.y }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{dna}{insight && insight.nearest_quote ? `\n\nClosest Shakespeare (pilot): "${insight.nearest_quote.text}" (${insight.nearest_quote.cite})` : ''}</pre>
        </div>
      )}
    </div>
  );
}

// Build DNA focused on rhetorical “ingredients” from a single line (apothecary style)
function buildLineDNAText(author, text, topPosts) {
  try {
    const t = (text || '').toLowerCase();
    const chips = [];
    const meter = (v) => {
      const n = Math.max(0, Math.min(10, Math.round(v * 10)));
      return '▰'.repeat(n) + '▏'.repeat(10 - n);
    };
    // Heuristic nodes only (suppress near-zeros)
    const nodeSports = /(coach|team|match|score|goal|ship)/.test(t) ? 0.7 : 0.0;
    const nodeHonor = /(\bhonor\b|\bvow\b|\bduty\b|\bfaith\b)/.test(t) ? 0.5 : 0.0;
    const nodeFate = /(\bfate\b|\bfortune\b|omin|prophec|\bdoom\b)/.test(t) ? 0.4 : 0.0;
    // Link to current top post if this line closely matches
    let reactionLine = '';
    try {
      const tops = Array.isArray(topPosts) ? topPosts : [];
      const best = tops
        .map(p => ({ url: p.scene_url || p.url, content: (p.content || ''), score: jaccardLike(p.content || '', text || '') }))
        .sort((a,b) => b.score - a.score)[0];
      if (best && best.score > 0.25 && best.url) {
        reactionLine = `🏆 Related top post (+${(tops.find(tp => (tp.content||'')===best.content)?.reactions)||'?'}): ${best.url}`;
      }
    } catch {}
    chips.push(`🧬 Nodes`);
    if (nodeSports > 0.15) chips.push(`• sports ${meter(nodeSports)} (${nodeSports.toFixed(2)})`);
    if (nodeHonor > 0.15) chips.push(`• honor ${meter(nodeHonor)} (${nodeHonor.toFixed(2)})`);
    if (nodeFate > 0.15) chips.push(`• fate ${meter(nodeFate)} (${nodeFate.toFixed(2)})`);
    return `${author}\n${text}\n\n${chips.join('\n')}${reactionLine ? `\n${reactionLine}` : ''}`;
  } catch { return 'Rhetorical DNA unavailable'; }
}

// Lightweight token overlap scorer
function jaccardLike(a, b) {
  try {
    const ta = (a||'').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const tb = (b||'').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!ta.length || !tb.length) return 0;
    const sa = new Set(ta);
    const sb = new Set(tb);
    let inter = 0;
    for (const t of sa) if (sb.has(t)) inter++;
    const denom = Math.max(sa.size, sb.size);
    return inter / denom;
  } catch { return 0; }
}

function CurtainSparkle() {
  // simple shooting star with CSS animation
  return (
    <div className="sparkle-wrap">
      <div className="sparkle" />
      <div className="sparkle" style={{ animationDelay: '0.2s', top: '22%' }} />
      <div className="sparkle" style={{ animationDelay: '0.4s', top: '44%' }} />
    </div>
  );
}

function CurtainSequence({ onComplete }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const totalMs = 12000; // 12s hold for clearer sizzle
    const start = performance.now();
    let raf = 0;
    const doneTimer = setTimeout(() => { try { onComplete && onComplete(); } catch {} }, totalMs);
    const tick = (t) => {
      const e = Math.min(1, (t - start) / totalMs);
      setElapsed(e);
      if (e < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearTimeout(doneTimer); };
  }, [onComplete]);
  const bands = new Array(30).fill(0);
  return (
    <div />
  );
}

function DelayComplete({ ms, onDone }) {
  useEffect(() => {
    const id = setTimeout(() => { try { onDone && onDone(); } catch {} }, Math.max(0, ms|0));
    return () => clearTimeout(id);
  }, [ms, onDone]);
  return null;
}

function TypingRoster() {
  // Simulated avatars + typing indicators for left stream
  const [avatars, setAvatars] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/ui/chat/avatars`);
        const dj = await r.json();
        if (alive) setAvatars(dj || {});
      } catch {}
    })();
    return () => { alive = false };
  }, []);
  const actors = [
    { name: 'Falstaff', color: '#9bdcff', key: 'falstaff' },
    { name: 'Lady Macbeth', color: '#ff5c8a', key: 'lady macbeth' },
    { name: 'Iago', color: '#ff5c8a', key: 'iago' },
    { name: 'Miranda', color: '#9bdcff', key: 'miranda' },
    { name: 'Sir Andrew', color: '#ff5c8a', key: 'sir andrew' }
  ];
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase(p => (p + 1) % actors.length), 1800);
    return () => clearInterval(id);
  }, [actors.length]);
  return (
    <div>
      {actors.map((a, i) => (
        <div className="line typing-row" key={a.name} style={{ opacity: i <= phase ? 1 : 0.35 }}>
          <span className="avatar" style={{ backgroundImage: `url(${avatars[a.key]||''})` }} />
          <span style={{ color: a.color, fontWeight: 600, marginRight: 6 }}>{a.name}</span>
          <span className="dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function avatarFor(author, avatars) {
  const k = (author||'').toLowerCase();
  if (k.startsWith('bitbard')) return avatars['bitbard'] || '';
  if (k.startsWith('lady macbeth')) return avatars['lady macbeth'] || '';
  if (k.startsWith('sir andrew')) return avatars['sir andrew'] || '';
  if (k.startsWith('falstaff')) return avatars['falstaff'] || '';
  if (k.startsWith('miranda')) return avatars['miranda'] || '';
  if (k.startsWith('iago')) return avatars['iago'] || '';
  return '';
}


